import { Form, notification } from "antd";
import { RcFile } from "antd/es/upload";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ContractPayload,
    getCatalogFinGroupsThunk,
    PostContractCreateParams,
    postContractCreateThunk,
    PutContractUpdateParams,
    putContractUpdateThunk,
} from "tm-shared/api";
import { Contract } from "tm-shared/types";
import { SerializedFile, serializeFile } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useMarket } from "@/hooks/useMarket";
import {
    patchContractFormValues,
    setContractFormValues,
} from "@/store/slices/work";
import { ContractFormValues } from "@/types/form";

import { ContractFormFields } from "./components/ContractFormFields";
import { ContractTypeFields } from "./components/ContractTypeFields";
import { FileUploadSection } from "./components/FileUploadSection";

const EMPTY_VALUES: Partial<ContractFormValues> = {
    paybackPeriod: undefined,
    paymentTerms: undefined,
    placePrice: undefined,
    materialsPrice: undefined,
    deferredDelivery: undefined,
    deferredTermForm1: undefined,
    deferredTermForm2: undefined,
    costInCash: undefined,
    isAllFinGroups: false,
    finGroups: [],
    discountInPercentage: undefined,
    products: [],
};

interface AddContractProps {
    editMode?: boolean;
    contract?: Contract;
    fop?: string;
}

const AddContract: React.FC<AddContractProps> = ({
    editMode = false,
    contract,
    fop,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { marketId } = useParams();
    const { options } = useAppSelector((s) => s.work);
    const { contractFormValues } = useAppSelector((state) => state.work);

    const { market, marketLoading } = useMarket(marketId);

    const [form] = Form.useForm<ContractFormValues>();
    const [oldFiles, setOldFiles] = useState<string[]>(contract?.files ?? []);

    const initialValues = useMemo<Partial<ContractFormValues>>(() => {
        if (editMode && contract) {
            const {
                files: _files,
                targetId: _marketId,
                id: _id,
                context,
                ...rest
            } = contract;
            return {
                ...rest,
                ...context,
            };
        }
        if (contractFormValues) {
            return contractFormValues;
        }
        return {};
    }, [editMode, contract, contractFormValues]);

    const handleDeleteFile = (file: string) => {
        setOldFiles((prev) => prev.filter((item) => item !== file));
    };

    const handleValuesChange = (changedValues: Partial<ContractFormValues>) => {
        const { files: _, ...values } = changedValues;

        if ("type" in changedValues) {
            form.setFieldsValue(EMPTY_VALUES);
        }

        dispatch(patchContractFormValues(values));
    };

    const [handleCreateContract, loading] = useAsyncAction(
        async (values: ContractFormValues) => {
            const {
                name,
                type,
                description,
                startDate,
                endDate,
                files,
                targetType,
                ...restValues
            } = values;

            const rcFiles = files
                ?.map((f) => {
                    return f.originFileObj as RcFile | undefined;
                })
                .filter((f): f is RcFile => !!f);

            let transformedFiles: SerializedFile[] | undefined;

            if (rcFiles?.length) {
                try {
                    transformedFiles = await Promise.all(
                        rcFiles.map((file) => serializeFile(file)),
                    );
                } catch (e) {
                    console.error("Помилка перетворення файлів:", e);
                }
            }

            const targetTypeVocabulary = {
                market: market?.id || "",
                network: market?.networkId || "",
                contragent: market?.contragent?.id || "",
                storage: market?.storage?.id || "",
                mainRegion: market?.address?.mainRegionId || "",
            };

            const request: ContractPayload = {
                name,
                type,
                targetType,
                targetId: targetTypeVocabulary[targetType] || market?.id || "",
                description,
                startDate,
                endDate,
                files: transformedFiles,
                context: {
                    ...restValues,
                },
            };

            try {
                if (editMode && contract) {
                    const filesToRemove = contract.files.filter(
                        (file) => !oldFiles.includes(file),
                    );

                    const editRequest: PutContractUpdateParams = {
                        ...request,
                        contractId: contract.id,
                        ...(filesToRemove.length && {
                            deleteFiles: filesToRemove,
                        }),
                    };

                    await dispatch(
                        putContractUpdateThunk(editRequest),
                    ).unwrap();
                    notification.success({ message: "Контракт оновлено" });
                } else {
                    const createRequest: PostContractCreateParams = {
                        ...request,
                    };

                    await dispatch(
                        postContractCreateThunk(createRequest),
                    ).unwrap();
                    notification.success({ message: "Контракт створений" });
                }

                dispatch(setContractFormValues(null));
                navigate(-1);
            } catch (error) {
                console.error(error);
            }
        },
    );

    useEffect(() => {
        if (!options?.finGroups || options?.finGroups?.length === 0) {
            dispatch(getCatalogFinGroupsThunk());
        }
    }, [dispatch]);

    return (
        <>
            {!editMode && (
                <Header title="Додати контракт" back titleFontSize={16} />
            )}

            <MainContainer>
                <div style={{ marginBottom: 16 }}>
                    {!editMode ? (
                        <SecondaryText fontSize={16}>
                            Внесіть необхідні дані для додавання контракту до
                            ТТ:
                        </SecondaryText>
                    ) : (
                        <PrimaryText fontSize={16}>{fop}</PrimaryText>
                    )}
                </div>

                <Form
                    form={form}
                    name="addContract"
                    style={{ width: "100%" }}
                    autoComplete="off"
                    requiredMark={false}
                    initialValues={initialValues}
                    onValuesChange={handleValuesChange}
                    layout="vertical"
                    onFinish={handleCreateContract}
                >
                    <ContractFormFields
                        editMode={editMode}
                        market={market}
                        marketLoading={marketLoading}
                    />

                    <ContractTypeFields />

                    <FileUploadSection
                        oldFiles={oldFiles}
                        onDeleteFile={handleDeleteFile}
                    />

                    <BaseButton
                        type="primary"
                        loading={loading}
                        disabled={loading}
                        htmlType="submit"
                    >
                        {editMode ? "Оновити контракт" : "Додати контракт"}
                    </BaseButton>
                </Form>
            </MainContainer>
        </>
    );
};

export default AddContract;
